import { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, UserCheck, Gift, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";

const TIER_COLORS = { Standard: "#64748B", Silver: "#94A3B8", Gold: "#0284C7", Platinum: "#0F8964", VIP: "#064E3B" };

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-mr rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <p className="label-eyebrow text-mr-secondary">{label}</p>
        <Icon className="h-4 w-4" style={{ color: accent }} />
      </div>
      <p className="font-display text-4xl font-bold text-mr-primary mt-3">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); }, []);

  if (!stats) {
    return (
      <AdminShell title="Overview">
        <div className="grid md:grid-cols-4 gap-6"><Skeleton className="h-32 rounded-2xl" /><Skeleton className="h-32 rounded-2xl" /><Skeleton className="h-32 rounded-2xl" /><Skeleton className="h-32 rounded-2xl" /></div>
      </AdminShell>
    );
  }
  const tierData = Object.entries(stats.tier_distribution).map(([name, value]) => ({ name, value }));

  return (
    <AdminShell title="Overview">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Kpi icon={Users} label="Total patients" value={stats.total_patients} accent="#0F8964" />
        <Kpi icon={UserCheck} label="Active (30d)" value={stats.active_patients} accent="#0EA572" />
        <Kpi icon={Gift} label="Redemptions" value={stats.total_redemptions} accent="#0284C7" />
        <Kpi icon={TrendingUp} label="Points issued" value={stats.points_issued.toLocaleString()} accent="#D9A441" />
      </div>

      <div className="grid lg:grid-cols-12 gap-6 mt-6">
        <div className="lg:col-span-7 bg-white border border-mr rounded-2xl p-6">
          <h2 className="font-display text-lg font-semibold text-mr-primary mb-4">New patients · last 7 days</h2>
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.enrollments_7d}>
                <XAxis dataKey="day" stroke="#59665F" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#59665F" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#E2E8F0" }} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="count" fill="#0F8964" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-5 bg-white border border-mr rounded-2xl p-6">
          <h2 className="font-display text-lg font-semibold text-mr-primary mb-4">Tier distribution</h2>
          <div className="h-60">
            {tierData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={tierData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80}>
                    {tierData.map((d) => <Cell key={d.name} fill={TIER_COLORS[d.name] || "#0F8964"} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-mr-secondary text-sm py-10 text-center">No patients yet.</p>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-white border border-mr rounded-2xl p-6">
          <h2 className="font-display text-lg font-semibold text-mr-primary mb-4">Top loyal patients</h2>
          <ul className="divide-y divide-mr-border">
            {stats.top_patients.map((p, i) => (
              <li key={p.id} className="py-3 flex items-center justify-between" data-testid={`top-patient-${p.id}`}>
                <div className="flex items-center gap-3">
                  <span className="h-8 w-8 rounded-full bg-[#E2E8F0] text-mr-primary flex items-center justify-center font-display font-semibold text-sm">{i + 1}</span>
                  <div>
                    <p className="text-sm font-medium text-mr-primary">{p.name}</p>
                    <p className="text-xs text-mr-secondary">{p.tier}</p>
                  </div>
                </div>
                <span className="font-display font-bold text-mr-primary">{p.points.toLocaleString()}</span>
              </li>
            ))}
            {stats.top_patients.length === 0 && <p className="text-mr-secondary text-sm py-6 text-center">No patients yet.</p>}
          </ul>
        </div>

        <div className="bg-white border border-mr rounded-2xl p-6">
          <h2 className="font-display text-lg font-semibold text-mr-primary mb-4">Recent activity</h2>
          <ul className="divide-y divide-mr-border">
            {stats.recent_activity.map((tx) => (
              <li key={tx.id} className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-mr-primary">{tx.user_name}</p>
                  <p className="text-xs text-mr-secondary">{tx.reason}</p>
                </div>
                <span className={`font-display font-semibold text-sm ${tx.points > 0 ? "text-mr-success" : "text-mr-accent"}`}>{tx.points > 0 ? "+" : ""}{tx.points}</span>
              </li>
            ))}
            {stats.recent_activity.length === 0 && <p className="text-mr-secondary text-sm py-6 text-center">No activity yet.</p>}
          </ul>
        </div>
      </div>
    </AdminShell>
  );
}
